import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Heart, ArrowRight, Eye, EyeOff, Globe, HandHeart, Briefcase, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login efetuado com sucesso!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0066CC] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
            <rect width="100" height="100" fill="url(#grid)"/>
          </svg>
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight">ENTRAJUDATA</span>
            </div>
            <p className="text-white/70 text-sm ml-1">Plataforma de Gestão de Dados</p>
          </div>
          
          <div className="space-y-8">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              Solidariedade<br />
              <span className="text-[#00AA66]">em dados</span>
            </h1>
            <p className="text-white/80 text-lg max-w-md leading-relaxed">
              Apoiamos mais de 2.100 instituições sociais que servem cerca de 885.000 pessoas em Portugal.
            </p>
            
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#00AA66]">2.100+</div>
                <div className="text-sm text-white/70 mt-1">Instituições</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#00AA66]">885K</div>
                <div className="text-sm text-white/70 mt-1">Pessoas Apoiadas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#00AA66]">18</div>
                <div className="text-sm text-white/70 mt-1">Distritos</div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <Building2 className="w-4 h-4" />
            <span>ENTRAJUDA - Associação para o Apoio a Instituições de Solidariedade Social</span>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#0066CC] rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">ENTRAJUDATA</span>
          </div>
          
          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-slate-900">Bem-vindo</CardTitle>
              <CardDescription className="text-slate-500">
                Introduza as suas credenciais para aceder à plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu.email@exemplo.pt"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0066CC] hover:bg-[#0052a3] text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      A entrar...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Entrar
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Portal Links */}
          <div className="mt-8">
            <p className="text-center text-sm text-slate-600 mb-4 font-medium">Outros Acessos</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Linha 1 - Portais principais */}
              <Link 
                to="/portal/login" 
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-emerald-500 hover:shadow-lg transition-all group"
              >
                <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center group-hover:bg-emerald-100 flex-shrink-0">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">Portal Instituições</p>
                  <p className="text-xs text-slate-500">Self-service</p>
                </div>
              </Link>
              
              <Link 
                to="/visitare/login" 
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-cyan-500 hover:shadow-lg transition-all group"
                data-testid="visitare-link"
              >
                <div className="w-11 h-11 bg-cyan-50 rounded-xl flex items-center justify-center group-hover:bg-cyan-100 flex-shrink-0">
                  <ClipboardCheck className="w-5 h-5 text-cyan-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">Portal Visitare</p>
                  <p className="text-xs text-slate-500">Visitadores</p>
                </div>
              </Link>
              
              <Link 
                to="/publico/voluntariado" 
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-purple-500 hover:shadow-lg transition-all group"
              >
                <div className="w-11 h-11 bg-purple-50 rounded-xl flex items-center justify-center group-hover:bg-purple-100 flex-shrink-0">
                  <HandHeart className="w-5 h-5 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">Portal Voluntário</p>
                  <p className="text-xs text-slate-500">Candidatar-se</p>
                </div>
              </Link>
              
              <Link 
                to="/doador/login" 
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-rose-500 hover:shadow-lg transition-all group"
                data-testid="portal-doador-link"
              >
                <div className="w-11 h-11 bg-rose-50 rounded-xl flex items-center justify-center group-hover:bg-rose-100 flex-shrink-0">
                  <Heart className="w-5 h-5 text-rose-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">Portal do Doador</p>
                  <p className="text-xs text-slate-500">Área pessoal</p>
                </div>
              </Link>
              
              {/* Linha central - Portal Público ocupa toda a largura em mobile */}
              <Link 
                to="/publico" 
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-amber-500 hover:shadow-lg transition-all group sm:col-span-2"
              >
                <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center group-hover:bg-amber-100 flex-shrink-0">
                  <Globe className="w-5 h-5 text-amber-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 text-sm">Portal Público</p>
                  <p className="text-xs text-slate-500">Dados abertos e transparência</p>
                </div>
              </Link>
            </div>
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-6">
            &copy; {new Date().getFullYear()} ENTRAJUDA. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
}
